import { Dispatch, SetStateAction, useMemo } from "react"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"

import {
    AccountingBillReceiversResponse,
    BillReceiversQuery,
    fetchAccountingBillReceivers,
} from "../../../accounting/bill-receivers/billReceiversApi"
import { BillReceiverType } from "../userGroupsTypes"
import { AddBillReceiverDialogCore } from "../../../../../../shared/components/billReceivers/dialog/AddBillReceiverDialogCore"
import { BillReceiversItem } from "../../../accounting/bill-receivers/billReceiversTypes"
import {
    BillReceiverEndpointVM,
    BillReceiverListItemVM,
} from "../../../../../../shared/components/billReceivers/billReceiverTypes"

type AddBillReceiverProps = Readonly<{
    type: "new" | "update"
    searchText?: string
    companyId: number
    handleChooseBillReceiver: (receiver: BillReceiversItem) => void
    dialogState: boolean
    setDialogState: Dispatch<SetStateAction<boolean>>
    handleCreateNewBill: () => void
}>

const selectBillReceivers = (data: AccountingBillReceiversResponse) => data.billReceivers

function toBillReceiverListItemVM(receiver: BillReceiversItem): BillReceiverListItemVM {
    const endpoint: BillReceiverEndpointVM =
        receiver.endpoint.type === BillReceiverType.EAN_NUMBER
            ? { kind: "ean", value: receiver.endpoint.eanNumber }
            : { kind: "email", value: receiver.endpoint.email }

    return {
        id: String(receiver.id),
        name: receiver.name,
        cvr: receiver.cvrNumber || undefined,
        endpoint,
        address: receiver.addressDisplayText || undefined,
    }
}

export function AddBillReceiver({
    type,
    searchText = "",
    companyId,
    handleChooseBillReceiver,
    dialogState,
    setDialogState,
    handleCreateNewBill,
}: AddBillReceiverProps) {
    const { data = [] } = useApiQuery(fetchAccountingBillReceivers, {
        queryName: BillReceiversQuery.LIST,
        dependencies: {
            companyId,
        },
        select: selectBillReceivers,
    })

    const receiverById = useMemo(() => new Map(data.map((receiver) => [String(receiver.id), receiver])), [data])
    const items = useMemo(() => data.map(toBillReceiverListItemVM), [data])

    return (
        <AddBillReceiverDialogCore
            type={type}
            searchText={searchText}
            items={items}
            dialogState={dialogState}
            setDialogState={setDialogState}
            onChooseBillReceiver={(receiverVm) => {
                const receiver = receiverById.get(receiverVm.id)
                if (receiver) {
                    handleChooseBillReceiver(receiver)
                }
            }}
            onCreateNewBill={handleCreateNewBill}
        />
    )
}
